import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ReactReduxContext } from 'react-redux';
import { AspectRatio } from '@ff-os/shared-react/components/aspect-ratio/AspectRatio';
import { ButtonWithIcon } from '@ff-os/shared-react/components/buttons/button-with-icon/ButtonWithIcon';
import { IconButton } from '@ff-os/shared-react/components/buttons/icon-button/IconButton';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { ArPreviewStatus } from '@ff-scene-plugins/gift-redux/slices/uiSlice';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { Config3d } from '@ff-scene-plugins/gift-shared/GiftVariantOptions';
import { setArPreviewStatus, setIsArQrWindowOpen } from '../_redux-ui';
import { selectConfig3d, selectShowArStatus } from '../_redux-variants';
import { activateAr } from './helpers/ar-preview/activateAr';
import { importPreview3d } from './helpers/importPreview3d';
import styles from './ArPreviewButton.module.scss';

export const _ArPreviewButton = ({ config3d, arPreviewStatus, updateArStatus, setIsArQrWindowOpen }: Props) => {

    const { t } = useTranslation();
    const { store } = useContext(ReactReduxContext);

    useEffect(() => {

        let cancelledAsync = false;
        ((async () => {
            const preview3d = await importPreview3d();
            if (config3d.isArEnabled && !cancelledAsync) {
                updateArStatus(preview3d.isArAvailable() ? 'ready' : 'qrcode');
            }
        })());

        return () => {
            cancelledAsync = true;
        };
    }, [config3d, updateArStatus]);

    const startArPreview = async () => {
        try {
            updateArStatus('loading');
            await activateAr(store.getState());
            updateArStatus('ready');
        } catch {
            updateArStatus('qrcode');
        }
    };

    if (['pending', 'disabled'].includes(arPreviewStatus)) {
        return null;
    }

    if (arPreviewStatus === 'loading') {
        return (
            <IconButton small
                className={styles.button}>
                <AspectRatio>
                    <Spinner overlaySize='full' spinnerSize='small' />
                </AspectRatio>
            </IconButton>
        );
    }

    const onClick = arPreviewStatus === 'ready'
        ? startArPreview
        : () => setIsArQrWindowOpen(true);

    return (
        <>
            <ButtonWithIcon
                className={styles.button}
                icon='3d-cube'
                style='ghost'
                onClick={onClick}>
                {t(`windows.ar_preview.ar`)}
            </ButtonWithIcon>
        </>
    );
};

type Props = {
    config3d: Config3d;
    arPreviewStatus: ArPreviewStatus;
    updateArStatus: (status: ArPreviewStatus) => void;
    setIsArQrWindowOpen: (value: boolean) => void;
};

const mapStateToProps = (state: GiftPdpState) => ({
    config3d: selectConfig3d(state)!,
    arPreviewStatus: selectShowArStatus(state)
});

const mapDispatchToState = (dispatch: GiftPdpDispatch) => ({
    updateArStatus: (status: ArPreviewStatus) => dispatch(setArPreviewStatus(status)),
    setIsArQrWindowOpen: (value: boolean) => dispatch(setIsArQrWindowOpen(value))
});

export const ArPreviewButton = connect(
    mapStateToProps,
    mapDispatchToState
)(_ArPreviewButton);